import React, { useState, useRef, useEffect, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { Button } from "@/components/ui/button"
import { MousePointer2, Pen, Highlighter, Eraser, Trash2, Crosshair, PenLine } from "lucide-react" // Crosshair as Laser alternative icon
import { cn } from "@/lib/utils"
import { WhiteboardModal } from "@/components/recording/whiteboard-modal"

export type AnnotationTool = 'cursor' | 'pen' | 'highlighter' | 'laser' | 'eraser'

interface Point {
    x: number
    y: number
}

export function AnnotationOverlay() {
    const canvasRef = useRef<HTMLCanvasElement>(null)
    const containerRef = useRef<HTMLDivElement>(null)
    const [tool, setTool] = useState<AnnotationTool>('cursor')
    const [isDrawing, setIsDrawing] = useState(false)
    const lastPoint = useRef<Point | null>(null)
    const [whiteboardOpen, setWhiteboardOpen] = useState(false)

    // Laser state
    const laserPoints = useRef<{ x: number, y: number, age: number }[]>([])
    const animationFrameId = useRef<number | null>(null)

    // Colors
    const PEN_COLOR = "#ef4444" // Red-500
    const HIGHLIGHTER_COLOR = "rgba(253, 224, 71, 0.4)" // Yellow-300 transparent
    const LASER_COLOR = "#ff0000"

    // Resize canvas
    useEffect(() => {
        const handleResize = () => {
            if (canvasRef.current) {
                canvasRef.current.width = window.innerWidth
                canvasRef.current.height = window.innerHeight
            }
        }
        window.addEventListener('resize', handleResize)
        handleResize()
        return () => window.removeEventListener('resize', handleResize)
    }, [])

    // Draw Logic
    const draw = useCallback((e: React.PointerEvent | PointerEvent) => {
        const canvas = canvasRef.current
        if (!canvas) return
        const ctx = canvas.getContext('2d')
        if (!ctx) return

        const x = e.clientX
        const y = e.clientY

        if (tool === 'laser') {
            laserPoints.current.push({ x, y, age: 1.0 })
            return
        }

        if (!isDrawing || !lastPoint.current) return

        ctx.lineJoin = 'round'
        ctx.lineCap = 'round'

        if (tool === 'pen') {
            ctx.globalCompositeOperation = 'source-over'
            ctx.strokeStyle = PEN_COLOR
            ctx.lineWidth = 3
        } else if (tool === 'highlighter') {
            ctx.globalCompositeOperation = 'multiply' // Better highlighting effect if background was white, but standard source-over with alpha is safer for overlays
            ctx.globalCompositeOperation = 'source-over'
            ctx.strokeStyle = HIGHLIGHTER_COLOR
            ctx.lineWidth = 20
        } else if (tool === 'eraser') {
            ctx.globalCompositeOperation = 'destination-out'
            ctx.lineWidth = 20
        }

        ctx.beginPath()
        ctx.moveTo(lastPoint.current.x, lastPoint.current.y)
        ctx.lineTo(x, y)
        ctx.stroke()

        lastPoint.current = { x, y }
    }, [isDrawing, tool])

    // Laser Animation Loop
    useEffect(() => {
        const renderLoop = () => {
            if (tool === 'laser' && canvasRef.current) {
                const ctx = canvasRef.current.getContext('2d')
                if (ctx) {
                    // We need to clear the laser trail specifically, but we don't want to clear drawings...
                    // Actually, if we use laser, we are likely JUST pointing. 
                    // BUT if we want to support drawing AND laser, we need a separate layer for laser.
                    // For simplicity: Laser is ephemeral. It's drawn on the same canvas. 
                    // To avoid clearing annotations, we probably need a second canvas layer or just accept laser is "top" and we can't easily clear JUST laser without redraw.
                    // Let's use a 2nd canvas for the Laser/Temporary effects? 
                    // OR: simpler approach for MVP: Laser clears the whole frame? No, that deletes notes.
                    // Better: Laser is just a DOM element following cursor? 
                    // No, that won't be recorded by screen recorder easily if it's just a div (Record Screen usually captures DOM, so yes it will).
                    // BUT `getDisplayMedia` captures the screen. A custom cursor DIV is the easiest way to ensure high perf without canvas clearing issues.
                    // Let's TRY Draw for now. 
                    // Actually, let's treat Laser as a "Draw" tool that disappears? 
                    // Issue: clearing "old" laser segments requires clearing the canvas area.
                    // Let's go with DIV cursor for Laser. It's robust for screen recording.
                }
            }
            // animationFrameId.current = requestAnimationFrame(renderLoop)
        }
        // renderLoop()
        // return () => cancelAnimationFrame(animationFrameId.current!)
    }, [tool])

    // Event Handlers
    const startDrawing = (e: React.PointerEvent) => {
        if (tool === 'cursor' || tool === 'laser') return
        setIsDrawing(true)
        lastPoint.current = { x: e.clientX, y: e.clientY }
    }

    const stopDrawing = () => {
        setIsDrawing(false)
        lastPoint.current = null
    }

    const clearCanvas = () => {
        if (canvasRef.current) {
            const ctx = canvasRef.current.getContext('2d')
            ctx?.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height)
        }
    }

    // Toolbar Drag State
    const [toolbarPos, setToolbarPos] = useState({
        x: typeof window !== 'undefined' ? window.innerWidth / 2 : 0,
        y: typeof window !== 'undefined' ? window.innerHeight - 120 : 0
    })
    const [isDraggingToolbar, setIsDraggingToolbar] = useState(false)
    const dragOffset = useRef({ x: 0, y: 0 })

    // Toolbar Drag Handlers
    const handleToolbarMouseDown = (e: React.MouseEvent) => {
        if (e.target instanceof HTMLButtonElement || (e.target as HTMLElement).closest('button')) return
        setIsDraggingToolbar(true)
        dragOffset.current = {
            x: e.clientX - toolbarPos.x,
            y: e.clientY - toolbarPos.y
        }
    }

    useEffect(() => {
        const handleGlobalMouseMove = (e: MouseEvent) => {
            if (isDraggingToolbar) {
                setToolbarPos({
                    x: e.clientX - dragOffset.current.x,
                    y: e.clientY - dragOffset.current.y
                })
            }
        }

        const handleGlobalMouseUp = () => {
            setIsDraggingToolbar(false)
        }

        if (isDraggingToolbar) {
            window.addEventListener('mousemove', handleGlobalMouseMove)
            window.addEventListener('mouseup', handleGlobalMouseUp)
        }

        return () => {
            window.removeEventListener('mousemove', handleGlobalMouseMove)
            window.removeEventListener('mouseup', handleGlobalMouseUp)
        }
    }, [isDraggingToolbar])

    if (typeof document === 'undefined') return null

    return createPortal(
        <div
            ref={containerRef}
            className="fixed inset-0 z-[9998] pointer-events-none"
        >
            {/* Drawing Canvas - only active when drawing tools selected */}
            <canvas
                ref={canvasRef}
                className={cn(
                    "absolute inset-0 w-full h-full touch-none",
                    tool !== 'cursor' ? "pointer-events-auto cursor-crosshair" : "pointer-events-none"
                )}
                onPointerDown={startDrawing}
                onPointerMove={draw}
                onPointerUp={stopDrawing}
                onPointerOut={stopDrawing}
                onPointerCancel={stopDrawing}
            />

            {/* Laser Pointer (Div follower) */}
            {tool === 'laser' && (
                <LaserPointer />
            )}

            {/* Toolbar - draggable & interactive */}
            <div
                className={cn(
                    "absolute pointer-events-auto flex items-center gap-2 p-2 bg-background/90 backdrop-blur border rounded-full shadow-lg transition-shadow cursor-grab active:cursor-grabbing select-none",
                    isDraggingToolbar && "shadow-xl scale-[1.02]"
                )}
                style={{
                    left: toolbarPos.x,
                    top: toolbarPos.y,
                    transform: 'translate(-50%, 0)'
                }}
                onMouseDown={handleToolbarMouseDown}
            >
                <div className="absolute left-0 top-0 w-4 h-full flex items-center justify-center opacity-50 cursor-grab">
                    <div className="w-1 h-4 bg-border/50 rounded-full" />
                </div>
                <div className="pl-3 flex items-center gap-2">
                    <ToolButton icon={MousePointer2} label="Cursor" active={tool === 'cursor'} onClick={() => setTool('cursor')} />
                    <div className="w-px h-6 bg-border mx-1" />
                    <ToolButton icon={Pen} label="Pen" active={tool === 'pen'} onClick={() => setTool('pen')} className="text-red-500" />
                    <ToolButton icon={Highlighter} label="Highlight" active={tool === 'highlighter'} onClick={() => setTool('highlighter')} className="text-yellow-500" />
                    <ToolButton icon={Crosshair} label="Laser" active={tool === 'laser'} onClick={() => setTool('laser')} className="text-red-600 animate-pulse" />
                    <ToolButton icon={Eraser} label="Eraser" active={tool === 'eraser'} onClick={() => setTool('eraser')} />
                    <div className="w-px h-6 bg-border mx-1" />
                    <ToolButton icon={Trash2} label="Clear" active={false} onClick={clearCanvas} variant="destructive" />
                    <div className="w-px h-6 bg-border mx-1" />
                    <ToolButton icon={PenLine} label="Whiteboard" active={whiteboardOpen} onClick={() => setWhiteboardOpen(w => !w)} className="text-indigo-500" />
                </div>
            </div>

            {/* Whiteboard Modal */}
            <WhiteboardModal open={whiteboardOpen} onClose={() => setWhiteboardOpen(false)} />
        </div>,
        document.body
    )
}

function ToolButton({ icon: Icon, label, active, onClick, className, variant = 'ghost' }: any) {
    return (
        <Button
            variant={active ? "secondary" : "ghost"}
            size="icon"
            className={cn(
                "h-8 w-8 rounded-full",
                active && "bg-primary/10 hover:bg-primary/20",
                variant === 'destructive' && "text-destructive hover:bg-destructive/10",
                className
            )}
            onClick={onClick}
            title={label}
        >
            <Icon className="h-4 w-4" />
        </Button>
    )
}

function LaserPointer() {
    const ref = useRef<HTMLDivElement>(null)

    useEffect(() => {
        const move = (e: MouseEvent) => {
            if (ref.current) {
                ref.current.style.transform = `translate(${e.clientX}px, ${e.clientY}px)`
            }
        }
        window.addEventListener('mousemove', move)
        return () => window.removeEventListener('mousemove', move)
    }, [])

    return (
        <div
            ref={ref}
            className="fixed top-0 left-0 w-4 h-4 bg-red-500 rounded-full shadow-[0_0_10px_2px_rgba(255,0,0,0.8)] pointer-events-none z-[9999] -mt-2 -ml-2 mix-blend-screen transition-transform duration-75 ease-out"
        />
    )
}
